/*
 * A site's sections, grouped by job: what the site bar in the header shows
 * (Overview, Pages, Growth, Settings) and the sub-tabs inside Growth and
 * Settings. Every route keeps its old URL; this only decides how they are
 * grouped and which tab is active.
 *
 * Pure (no React, no react-router) so the matching is unit-tested in
 * siteNav.test.js: CRA's Jest cannot load react-router-dom v7.
 */

const directoryShown = (domain, active) => active || !!(domain && domain.directory && domain.directory.enabled);

export const SITE_SECTIONS = [
  { key: 'overview', label: 'Overview', path: '' },
  { key: 'pages', label: 'Pages', path: '/pages' },
  {
    key: 'growth',
    label: 'Growth',
    path: '/rankings',
    hint: 'Search rankings, Amazon offers and products',
    children: [
      { key: 'rankings', label: 'Rankings', path: '/rankings' },
      { key: 'offers', label: 'Offers', path: '/offers' },
      { key: 'products', label: 'Products', path: '/products' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    hint: 'Featured pages, categories, domain info, redirects and directory',
    children: [
      { key: 'site', label: 'Site settings', path: '/settings' },
      { key: 'redirects', label: 'Redirects', path: '/redirects' },
      { key: 'directory', label: 'Directory', path: '/directory', shown: directoryShown },
    ],
  },
];

export const siteBase = (domainId) => `/domain/${encodeURIComponent(domainId)}`;

/* The editor's own route for a new page; it is a button, not the Pages tab. */
export const newPagePath = (domainId) => `${siteBase(domainId)}/pages/new/edit`;

const under = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/* Which section (and sub-tab) a pathname belongs to for this site:
   -> { section: 'overview'|'pages'|'growth'|'settings'|null, child: key|null } */
export const activeSiteSection = (pathname, domainId) => {
  const none = { section: null, child: null };
  if (!domainId) return none;
  // the router hands back decoded paths; accept either form of the id
  const bases = [...new Set([siteBase(domainId), `/domain/${domainId}`])];
  const base = bases.find((b) => under(pathname, b));
  if (!base) return none;
  const rest = pathname.slice(base.length).replace(/\/+$/, '');
  if (rest === '') return { section: 'overview', child: null };
  if (rest === '/pages/new/edit') return none;
  for (const s of SITE_SECTIONS) {
    if (s.children) {
      const child = s.children.find((c) => under(rest, c.path));
      if (child) return { section: s.key, child: child.key };
    } else if (s.path && under(rest, s.path)) {
      return { section: s.key, child: null };
    }
  }
  return none;
};

/* The sub-tabs of a section for this site, each with its URL and active flag. */
export const sectionTabs = (sectionKey, domainId, domain, pathname = '') => {
  const section = SITE_SECTIONS.find((s) => s.key === sectionKey);
  if (!section || !section.children) return [];
  const active = activeSiteSection(pathname, domainId);
  return section.children
    .map((c) => ({ ...c, to: `${siteBase(domainId)}${c.path}`, active: active.section === sectionKey && active.child === c.key }))
    .filter((c) => !c.shown || c.shown(domain, c.active));
};
